import React from 'react';
import { useTraderPermissions } from '../hooks/useTraderPermissions';
import { ModulePermission, PermissionAction } from '../types/permissions';

interface TraderPermissionGateProps {
    module: ModulePermission;
    action: PermissionAction;
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

interface TraderPermissionGroupProps {
    permissions: Array<{ module: ModulePermission; action: PermissionAction }>;
    requireAll?: boolean;
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

/**
 * Trader-specific permission gate component
 * Provides easy permission checking for trader-related features
 */
export const TraderPermissionGate: React.FC<TraderPermissionGateProps> = ({
    module,
    action,
    children,
    fallback = null,
}) => {
    const { hasPermission } = useTraderPermissions();

    // Map module to specific permission check
    const checkPermission = () => {
        switch (module) {
            case ModulePermission.ORDERS:
                switch (action) {
                    case PermissionAction.VIEW:
                        return hasPermission(ModulePermission.ORDERS, PermissionAction.VIEW);
                    case PermissionAction.CREATE:
                        return hasPermission(ModulePermission.ORDERS, PermissionAction.CREATE);
                    case PermissionAction.EDIT:
                        return hasPermission(ModulePermission.ORDERS, PermissionAction.EDIT);
                    case PermissionAction.DELETE:
                        return hasPermission(ModulePermission.ORDERS, PermissionAction.DELETE);
                    case PermissionAction.EXPORT:
                        return hasPermission(ModulePermission.ORDERS, PermissionAction.EXPORT);
                    default:
                        return false;
                }
            case ModulePermission.TRADES:
                switch (action) {
                    case PermissionAction.VIEW:
                        return hasPermission(ModulePermission.TRADES, PermissionAction.VIEW);
                    case PermissionAction.CREATE:
                        return hasPermission(ModulePermission.TRADES, PermissionAction.CREATE);
                    case PermissionAction.EDIT:
                        return hasPermission(ModulePermission.TRADES, PermissionAction.EDIT);
                    case PermissionAction.DELETE:
                        return hasPermission(ModulePermission.TRADES, PermissionAction.DELETE);
                    case PermissionAction.EXPORT:
                        return hasPermission(ModulePermission.TRADES, PermissionAction.EXPORT);
                    default:
                        return false;
                }
            case ModulePermission.TRADING:
                switch (action) {
                    case PermissionAction.VIEW:
                        return hasPermission(ModulePermission.TRADING, PermissionAction.VIEW);
                    case PermissionAction.CREATE:
                        return hasPermission(ModulePermission.TRADING, PermissionAction.CREATE);
                    case PermissionAction.EDIT:
                        return hasPermission(ModulePermission.TRADING, PermissionAction.EDIT);
                    case PermissionAction.DELETE:
                        return hasPermission(ModulePermission.TRADING, PermissionAction.DELETE);
                    default:
                        return false;
                }
            case ModulePermission.TRADING_ACCOUNTS:
                switch (action) {
                    case PermissionAction.VIEW:
                        return hasPermission(
                            ModulePermission.TRADING_ACCOUNTS,
                            PermissionAction.VIEW
                        );
                    case PermissionAction.EDIT:
                        return hasPermission(
                            ModulePermission.TRADING_ACCOUNTS,
                            PermissionAction.EDIT
                        );
                    default:
                        return false;
                }
            case ModulePermission.TRANSACTIONS:
                switch (action) {
                    case PermissionAction.VIEW:
                        return hasPermission(ModulePermission.TRANSACTIONS, PermissionAction.VIEW);
                    case PermissionAction.EDIT:
                        return hasPermission(ModulePermission.TRANSACTIONS, PermissionAction.EDIT);
                    default:
                        return false;
                }
            default:
                return false;
        }
    };

    const hasAccess = checkPermission();
    return hasAccess ? <>{children}</> : <>{fallback}</>;
};

/**
 * Trader permission group component
 * Allows checking multiple permissions at once
 */
export const TraderPermissionGroup: React.FC<TraderPermissionGroupProps> = ({
    permissions,
    requireAll = false,
    children,
    fallback = null,
}) => {
    const { hasAnyPermission, hasAllPermissions } = useTraderPermissions();

    const hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);

    return hasAccess ? <>{children}</> : <>{fallback}</>;
};

// Convenience components for common trader actions
export const OrderViewGate: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
    children,
    fallback,
}) => (
    <TraderPermissionGate
        module={ModulePermission.ORDERS}
        action={PermissionAction.VIEW}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

export const OrderCreateGate: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
    <TraderPermissionGate
        module={ModulePermission.ORDERS}
        action={PermissionAction.CREATE}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

export const OrderEditGate: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
    children,
    fallback,
}) => (
    <TraderPermissionGate
        module={ModulePermission.ORDERS}
        action={PermissionAction.EDIT}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

export const OrderDeleteGate: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
    <TraderPermissionGate
        module={ModulePermission.ORDERS}
        action={PermissionAction.DELETE}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

export const TradeViewGate: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
    children,
    fallback,
}) => (
    <TraderPermissionGate
        module={ModulePermission.TRADES}
        action={PermissionAction.VIEW}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

export const TradeEditGate: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({
    children,
    fallback,
}) => (
    <TraderPermissionGate
        module={ModulePermission.TRADES}
        action={PermissionAction.EDIT}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

export const TradeDeleteGate: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
    <TraderPermissionGate
        module={ModulePermission.TRADES}
        action={PermissionAction.DELETE}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

export const PositionViewGate: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
    <TraderPermissionGate
        module={ModulePermission.TRADING}
        action={PermissionAction.VIEW}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

export const PositionEditGate: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
    <TraderPermissionGate
        module={ModulePermission.TRADING}
        action={PermissionAction.EDIT}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

export const TradingAccountViewGate: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
    <TraderPermissionGate
        module={ModulePermission.TRADING_ACCOUNTS}
        action={PermissionAction.VIEW}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

export const TransactionViewGate: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
    <TraderPermissionGate
        module={ModulePermission.TRANSACTIONS}
        action={PermissionAction.VIEW}
        fallback={fallback}
    >
        {children}
    </TraderPermissionGate>
);

// Analytics and export gates
export const TradingAnalyticsGate: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => {
    const { canViewTradingAnalytics } = useTraderPermissions();
    return canViewTradingAnalytics() ? <>{children}</> : <>{fallback}</>;
};

export const TradingExportGate: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => {
    const { canExportTradingData } = useTraderPermissions();
    return canExportTradingData() ? <>{children}</> : <>{fallback}</>;
};

export const TradingManagementGate: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => {
    const { canManageTrading } = useTraderPermissions();
    return canManageTrading() ? <>{children}</> : <>{fallback}</>;
};
